/**
 * Admin → Deals Users.
 *
 * Lets the admin grant scoped access to the Deals platform. A "deals user"
 * can sign in (Google OAuth, same flow as everyone else), but is bounced
 * to /admin/deals and can't see Dashboard, Trip Planner, Settings, or any
 * other /admin/* page.
 *
 * Add: enter an email → backend pre-provisions a user doc with
 * role="deals_user" and approves them on the waitlist so the next Google
 * sign-in attaches to that doc. If the email already exists, the role is
 * just set.
 * Revoke: clears the role and kills active sessions, so the change takes
 * effect immediately. The user record itself isn't deleted.
 */

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { UserPlus, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function AdminDealsUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addMsg, setAddMsg] = useState("");
  const [addErr, setAddErr] = useState("");

  const [confirmFor, setConfirmFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setLoadErr("");
    try {
      const { data } = await api.get("/admin/deals-users");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function add(e) {
    e?.preventDefault();
    const em = (email || "").trim().toLowerCase();
    if (!em || !em.includes("@")) return;
    setAdding(true);
    setAddMsg("");
    setAddErr("");
    try {
      const { data } = await api.post("/admin/deals-users", {
        email: em,
        name: (name || "").trim(),
      });
      setAddMsg(
        data?.created
          ? `Created ${em} with deals-only access.`
          : `Granted deals-only access to ${em}.`,
      );
      setEmail("");
      setName("");
      await load();
    } catch (err) {
      setAddErr(err?.response?.data?.detail || err?.message || "add failed");
    } finally {
      setAdding(false);
    }
  }

  async function revoke(uid) {
    setBusyId(uid);
    try {
      await api.delete(`/admin/deals-users/${uid}`);
      setConfirmFor(null);
      await load();
    } catch (err) {
      setLoadErr(err?.response?.data?.detail || err?.message || "revoke failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AdminLayout activeTab="deals-users" title="Deals Users">
      <div data-testid="admin-deals-users-page">
        <div className="section-eyebrow">Admin · Deals users</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">
          Deals-only access
        </h1>
        <p className="text-neutral-600 mt-2 max-w-2xl text-[14px] leading-relaxed">
          Add an email to grant access to the Deals platform only. These users
          sign in through Google and land on <code className="font-mono">/admin/deals</code>;
          they cannot reach the Dashboard or any other admin page.
        </p>

        <form
          onSubmit={add}
          className="card p-5 mt-6 max-w-2xl"
          data-testid="admin-deals-users-add"
        >
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold mb-3">
            Grant access
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-2">
            <input
              className="input"
              type="email"
              placeholder="partner@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-deals-users-email"
            />
            <input
              className="input"
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-deals-users-name"
            />
            <button
              type="submit"
              disabled={adding || !email}
              className="btn btn-primary text-[13px] inline-flex items-center gap-1.5"
              data-testid="admin-deals-users-submit"
            >
              {adding ? <span className="spinner" /> : <UserPlus size={14} />}
              {adding ? "Adding…" : "Add"}
            </button>
          </div>
          {addMsg && (
            <div
              className="mt-3 text-[12.5px] p-2.5 rounded-[8px] inline-flex items-center gap-2"
              style={{ background: "var(--good-bg)", color: "var(--good)" }}
              data-testid="admin-deals-users-add-msg"
            >
              <CheckCircle2 size={14} /> {addMsg}
            </div>
          )}
          {addErr && (
            <div
              className="mt-3 text-[12.5px] p-2.5 rounded-[8px] inline-flex items-center gap-2"
              style={{ background: "var(--bad-bg)", color: "var(--bad)" }}
              data-testid="admin-deals-users-add-err"
            >
              <AlertCircle size={14} /> {addErr}
            </div>
          )}
        </form>

        <div className="mt-8">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 font-semibold mb-3">
            Current deals users ({rows.length})
          </div>

          {loadErr && (
            <div
              className="mb-3 text-[12.5px] p-2.5 rounded-[8px] inline-flex items-center gap-2"
              style={{ background: "var(--bad-bg)", color: "var(--bad)" }}
            >
              <AlertCircle size={14} /> {loadErr}
            </div>
          )}

          <div className="card overflow-hidden" data-testid="admin-deals-users-table">
            <div className="grid grid-cols-12 px-5 py-3 text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 border-b border-black/5 bg-black/[0.02]">
              <div className="col-span-6">User</div>
              <div className="col-span-3">Added</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {loading && (
              <div className="px-5 py-8 text-[13px] text-neutral-500 text-center">
                <span className="spinner" /> Loading…
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div className="px-5 py-8 text-[13px] text-neutral-500 text-center">
                No deals users yet. Add one above.
              </div>
            )}
            {rows.map((u) => {
              const isConfirming = confirmFor === u.user_id;
              return (
                <div
                  key={u.user_id}
                  className="grid grid-cols-12 items-center px-5 py-3.5 border-b border-black/5 last:border-b-0 text-[13px]"
                  data-testid={`admin-deals-user-row-${u.user_id}`}
                >
                  <div className="col-span-6 min-w-0">
                    <div className="font-semibold truncate">{u.name || "—"}</div>
                    <div className="text-[11.5px] text-neutral-500 truncate">{u.email}</div>
                  </div>
                  <div className="col-span-3 text-neutral-600 text-[12px]">
                    {fmtDate(u.created_at)}
                  </div>
                  <div className="col-span-3 text-right">
                    {isConfirming ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          className="text-[11.5px] px-2 py-1 rounded text-neutral-500 hover:text-black"
                          onClick={() => setConfirmFor(null)}
                          disabled={busyId === u.user_id}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn text-[11.5px] py-1.5 px-2.5 inline-flex items-center gap-1"
                          style={{ background: "var(--bad)", color: "#fff" }}
                          onClick={() => revoke(u.user_id)}
                          disabled={busyId === u.user_id}
                          data-testid={`admin-deals-user-confirm-revoke-${u.user_id}`}
                        >
                          {busyId === u.user_id ? <span className="spinner" /> : <Trash2 size={11} />}
                          Revoke
                        </button>
                      </div>
                    ) : (
                      <button
                        className="text-neutral-500 hover:text-black p-1.5 rounded transition-colors inline-flex items-center gap-1 text-[11.5px]"
                        onClick={() => setConfirmFor(u.user_id)}
                        data-testid={`admin-deals-user-revoke-${u.user_id}`}
                      >
                        <Trash2 size={11} /> Revoke
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
